/**
 * Demo Wave 1 — First cinematic tutorial sequence.
 * "Show First, Explain After" pattern:
 *   1. Camera pans to base → alert dialogue
 *   2. Camera pans to spawn → enemies incoming dialogue
 *   3. Enemies spawn and approach
 *   4. Camera pans to placement tile → deploy turret dialogue
 *   5. Fake cursor clicks placement tile → robot auto-placed
 *   6. Wait for enemies to die or timeout
 *   7. Recap panel explaining what the player just saw
 */
import { registerSequence } from '../CinematicDirector';
import type { CinematicSequence } from '../CinematicTypes';

const demoWave1: CinematicSequence = {
  id: 'demo-wave-1',
  lockCamera: true,
  lockInput: true,
  targetScene: 'GameScene',
  steps: [
    // ── Act 1: Alert — The base is under attack ──
    {
      id: 'pan-base',
      type: 'cameraPan',
      target: 'base',
      zoom: 2.0,
      duration: 800,
    },
    {
      id: 'alert-line',
      type: 'dialogue',
      speaker: 'cinematic.speaker.operator',
      textKey: 'cinematic.dw1.alert',
      emotion: 'alert',
      position: 'top',
      portrait: 'char_voice_assistant',
      portraitDisplay: 'inBubble',
      autoDismissMs: 2500,
      delayBefore: 300,
    },

    // ── Act 2: Show enemy spawn ──
    {
      id: 'pan-spawn',
      type: 'cameraPan',
      target: 'spawn',
      zoom: 1.5,
      duration: 1000,
    },
    {
      id: 'enemy-line',
      type: 'dialogue',
      speaker: 'cinematic.speaker.operator',
      textKey: 'cinematic.dw1.enemyIncoming',
      emotion: 'alert',
      position: 'top',
      portrait: 'char_voice_assistant',
      portraitDisplay: 'inBubble',
      autoDismissMs: 2000,
      delayBefore: 200,
    },
    {
      id: 'spawn-enemies',
      type: 'action',
      action: 'spawnEnemies',
      params: { count: 3, type: 'grunt' },
      delayAfter: 500,
    },

    // ── Act 3: Let the player watch enemies approach ──
    {
      id: 'watch-approach',
      type: 'wait',
      timeoutMs: 3000,
    },

    // ── Act 4: Show solution — deploy a turret ──
    {
      id: 'pan-placement',
      type: 'cameraPan',
      target: 'path',
      zoom: 2.0,
      duration: 800,
    },
    {
      id: 'deploy-line',
      type: 'dialogue',
      speaker: 'cinematic.speaker.commander',
      textKey: 'cinematic.dw1.deployTurret',
      emotion: 'calm',
      position: 'bottom',
      portrait: 'char_mustang',
      portraitDisplay: 'inBubble',
      autoDismissMs: 2500,
      delayBefore: 300,
    },
    {
      id: 'fake-click-tile',
      type: 'fakeClick',
      target: 'placementTile',
      holdMs: 500,
      delayBefore: 200,
    },
    {
      id: 'auto-place-robot',
      type: 'action',
      action: 'placeRobot',
      params: { preset: 'Turret', col: 5, row: 7 },
      delayAfter: 500,
    },

    // ── Act 5: Watch combat resolve ──
    {
      id: 'wait-combat',
      type: 'wait',
      timeoutMs: 12000,
    },

    // ── Act 6: Recap — explain what the player just saw ──
    {
      id: 'recap',
      type: 'recap',
      titleKey: 'cinematic.dw1.recapTitle',
      items: [
        { textKey: 'cinematic.dw1.recapEnemies' },
        { textKey: 'cinematic.dw1.recapPath' },
        { textKey: 'cinematic.dw1.recapTurret' },
      ],
      confirmKey: 'cinematic.dw1.recapConfirm',
    },
  ],
};

// Self-register when imported
registerSequence(demoWave1, false);
